import api from "./api"
import { AxiosError } from "axios"
import {
  normalizeImportFailures,
  SpreadsheetImportResult,
} from "../utils/spreadsheetImportResult"

export type SpreadsheetImportKind = "employees" | "job-codes" | "job-sites"
export type SpreadsheetImportIssue = "columns" | "failed"
export type { SpreadsheetImportResult, SpreadsheetImportFailure } from "../utils/spreadsheetImportResult"

export class SpreadsheetImportError extends Error {
  issue: SpreadsheetImportIssue

  constructor(issue: SpreadsheetImportIssue, message = issue) {
    super(message)
    this.name = "SpreadsheetImportError"
    this.issue = issue
  }
}

function axiosStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "response" in error) {
    return (error as { response?: { status?: number } }).response?.status
  }
  return undefined
}

function axiosData(error: unknown): unknown {
  if (error && typeof error === "object" && "response" in error) {
    return (error as { response?: { data?: unknown } }).response?.data
  }
  return undefined
}

function resultFromBody(data: unknown): SpreadsheetImportResult {
  const body = (data && typeof data === "object" ? data : {}) as Record<string, unknown>
  return {
    imported: Number(body.imported ?? body.created ?? body.added ?? 0),
    skipped: Number(body.skipped ?? 0),
    failures: normalizeImportFailures(body),
  }
}

/**
 * Multipart ingest contract for ConstructionWalk workbook upload.
 * Companion API: POST /sage-100/imports/:kind
 *   form: file, managementCompanyId, optional jobSiteId
 * Mapping (API):
 *   employees → PR_5-2-1 Employees / Field Frontline
 *   job-codes → PJ_6-2 Budgets / Accurate Metal budget template
 *   job-sites → PJ_6-2 / PJ_6-3 (jobnum seeds)
 * Do not call payroll-records / daily-payroll ingest from this client.
 */
export async function importSpreadsheet(options: {
  kind: SpreadsheetImportKind
  file: File
  managementCompanyId: number
  jobSiteId?: number
}): Promise<SpreadsheetImportResult> {
  const body = new FormData()
  body.append("file", options.file)
  body.append("managementCompanyId", String(options.managementCompanyId))
  if (options.jobSiteId) {
    body.append("jobSiteId", String(options.jobSiteId))
  }

  try {
    const response = await api.post(`/sage-100/imports/${options.kind}`, body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    return resultFromBody(response.data)
  } catch (error) {
    const status = axiosStatus(error) ?? (error instanceof AxiosError ? error.response?.status : undefined)
    const result = resultFromBody(axiosData(error))
    if (result.failures.length || result.imported > 0) {
      return result
    }
    if (status === 400 || status === 422) {
      throw new SpreadsheetImportError("columns")
    }
    throw new SpreadsheetImportError("failed")
  }
}

export function isSpreadsheetFile(file: File): boolean {
  const name = file.name.toLowerCase()
  return name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xls")
}
